import React from "react";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import { useAdminPanelStore } from "features/AdminPanel/store/useAdminPanelStore";
import { useDesktopMediaQuery } from "features/Common/hooks/useDesktopMediaQuery";
import { validURLV2 } from "utils/validations";
import { ManageOfferingInputSubSection } from "../ManageOfferingInputSubSection/ManageOfferingInputSubSection";
import { AddCircleOutline, Close } from "@material-ui/icons";
import { UploadFileButton } from "../UploadFileButton/UploadFileButton";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import { useContractUpload } from "./utils/hooks/useContractUpload";
import {
  getUploadedFiles,
  getFileLabel,
  shouldShowUploadButtons,
  getRemainingUploadConfigs,
} from "./utils/contractUpload.utils";

export const ManageContractInputSubSection = withComponentLibraryTheme(
  ({
    subSectionKey,
    placeholder = "",
    defaultValue,
    fileNameLabel = "",
    multiUpload,
    endPoint,
  }) => {
    const isDesktop = useDesktopMediaQuery();
    const sectionConfig = useAdminPanelStore((state) => state.sectionConfig);
    const subSectionConfig = sectionConfig[subSectionKey] || {};
    const { value: selectedValue } = subSectionConfig;

    const { handleOnSuccessUpload, onClickClose, onClickPdf, listingId } =
      useContractUpload({ subSectionKey, endPoint });

    // Parse selectedValue - handle both string (legacy) and array (multi-upload)
    const uploadedFiles = getUploadedFiles(selectedValue);

    return (
      <div className={!isDesktop ? "tw-w-[100%] !tw-mt-[-4px]" : "tw-w-[43%]"}>
        <>
          {uploadedFiles.length > 0 ? (
            <>
              {uploadedFiles.map((fileUrl, index) => (
                <div
                  key={`${fileUrl}`}
                  onClick={(e) => onClickPdf(e, fileUrl)}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-w-full tw-rounded-lg tw-bg-gray-100 tw-px-5 tw-py-3 tw-mb-2"
                >
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <ExlyImage
                      className={"tw-w-5 tw-object-contain"}
                      src={getS3AssetCDNUrl(
                        "assets/vectors/dashboard/red_pdf.png"
                      )}
                    />

                    <span className="tw-text-sm -tw-ml-1 tw-font-medium tw-text-gray-900">
                      {getFileLabel(index, fileNameLabel, multiUpload)}
                    </span>
                  </div>

                  <div
                    onClick={(e) => onClickClose(e, index, uploadedFiles)}
                    className="tw-bg-[#787878] tw-rounded-full tw-w-5 tw-h-5 tw-aspect-square tw-flex tw-items-center tw-justify-center"
                  >
                    <Close fontSize="small" className="tw-text-white !tw-w-4" />
                  </div>
                </div>
              ))}

              {/* Show upload buttons if in multi-upload mode and haven't reached max */}
              {shouldShowUploadButtons(multiUpload, uploadedFiles) && (
                <div className="tw-flex tw-flex-col tw-gap-2">
                  {getRemainingUploadConfigs(multiUpload, uploadedFiles).map(
                    (uploadConfig, index) => (
                      <UploadFileButton
                        key={`${uploadConfig}`}
                        btnText={uploadConfig.btnText}
                        startIcon={AddCircleOutline}
                        accept="application/pdf"
                        onSuccess={(fileLocation) =>
                          handleOnSuccessUpload(
                            fileLocation,
                            uploadedFiles.length + index,
                            uploadedFiles,
                            multiUpload
                          )
                        }
                        className={isDesktop ? "!tw-text-[14px] tw-w-full" : ""}
                        baseFilePath={`contracts/${listingId}`}
                      />
                    )
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Only show URL input if not in multi-upload mode */}
              {!multiUpload && (
                <ManageOfferingInputSubSection
                  subSectionKey={subSectionKey}
                  defaultValue={defaultValue}
                  placeholder={placeholder}
                  parentWrapperClassName={
                    !isDesktop ? "tw-w-[100%] !tw-mt-[-4px]" : "tw-w-[100%]"
                  }
                  endPoint={endPoint}
                  validate={(value) => {
                    if (!value) return null;
                    if (!validURLV2(value)) {
                      return "Please enter a valid URL";
                    }
                    return null;
                  }}
                />
              )}

              {/* Show OR divider only if not in multi-upload mode */}
              {!multiUpload && (
                <div className="tw-flex tw-items-center tw-w-full tw-gap-4 tw-py-2">
                  <div className="tw-flex-1 tw-h-px tw-bg-gray-300"></div>
                  <span className="tw-text-gray-500 tw-text-sm tw-font-medium">
                    OR
                  </span>
                  <div className="tw-flex-1 tw-h-px tw-bg-gray-300"></div>
                </div>
              )}

              {multiUpload ? (
                <div className="tw-flex tw-flex-col tw-gap-2">
                  {multiUpload.map((uploadConfig, index) => (
                    <UploadFileButton
                      key={`${uploadConfig}`}
                      btnText={uploadConfig.btnText}
                      startIcon={AddCircleOutline}
                      accept="application/pdf"
                      onSuccess={(fileLocation) =>
                        handleOnSuccessUpload(
                          fileLocation,
                          index,
                          uploadedFiles,
                          multiUpload
                        )
                      }
                      className={isDesktop ? "!tw-text-[14px] tw-w-full" : ""}
                      baseFilePath={`contracts/${listingId}`}
                    />
                  ))}
                </div>
              ) : (
                <UploadFileButton
                  btnText="Upload document"
                  startIcon={AddCircleOutline}
                  accept="application/pdf"
                  onSuccess={(fileLocation) =>
                    handleOnSuccessUpload(
                      fileLocation,
                      undefined,
                      uploadedFiles,
                      multiUpload
                    )
                  }
                  className={isDesktop ? "!tw-text-[14px] tw-w-full" : ""}
                  baseFilePath={`contracts/${listingId}`}
                />
              )}
            </>
          )}
        </>
      </div>
    );
  }
);
